import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import z from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { useApiMutation } from '@/modules/common/hooks/useApiMutation';

import { SendSmsPayload } from '../../../../types';
import { SendSmsDialogSchema } from '../../../../validations';
import { ConfirmSendSmsDialog } from './confirm-send-sms-dialog';

interface SendSmsDialogProps {
  isOpen: boolean;
  eventId?: string;
  participantIds: string[];
  onOpenChange: (open: boolean) => void;
}

export type SendSmsDialogValues = z.infer<typeof SendSmsDialogSchema>;

export function SendSmsDialog({
  isOpen,
  onOpenChange,
  eventId,
  participantIds,
}: SendSmsDialogProps) {
  const [isConfirmSendSmsDialogOpen, setIsConfirmSendSmsDialogOpen] =
    useState(false);

  const form = useForm<SendSmsDialogValues>({
    resolver: zodResolver(SendSmsDialogSchema),
    defaultValues: {
      message: '',
    },
  });

  const sendSmsMutation = useApiMutation<SendSmsPayload>(
    `/events/${eventId}/participants/send-sms`,
    {
      method: 'POST',
    },
    {
      onError: (error) => {
        toast.error(error.message);
      },
    }
  );

  const sendSms = async () => {
    try {
      setIsConfirmSendSmsDialogOpen(false);
      const data = form.getValues();
      await sendSmsMutation.mutateAsync({
        message: data.message,
        participantIds,
      });
      toast.success('SMS sent successfully!');
      onOpenChange(false);
    } catch (error) {
      console.error(error.message);
    }
  };

  const handleFormSubmit = async () => {
    setIsConfirmSendSmsDialogOpen(true);
  };

  return (
    <>
      <Dialog onOpenChange={onOpenChange} open={isOpen}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>Send SMS Message</DialogTitle>
            <DialogDescription>
              Send an SMS message to selected participants.
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                event.stopPropagation();
                form.handleSubmit(handleFormSubmit)(event);
              }}
              className="py-6"
            >
              <div className="flex flex-col max-w-6xl">
                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Message</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Max 500 characters"
                          maxLength={500}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <DialogFooter className="mt-6 gap-4">
                <DialogClose asChild>
                  <Button
                    variant="outline"
                    className="w-[130px]"
                    data-testid="cancel-button"
                  >
                    Cancel
                  </Button>
                </DialogClose>
                <Button
                  type="submit"
                  className="w-[130px]"
                  data-testid="save-button"
                  isLoading={sendSmsMutation.isPending}
                >
                  Send SMS
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {isConfirmSendSmsDialogOpen && (
        <ConfirmSendSmsDialog
          isOpen={isConfirmSendSmsDialogOpen}
          onSubmit={sendSms}
          onOpenChange={setIsConfirmSendSmsDialogOpen}
        />
      )}
    </>
  );
}
